/**
 * Shared `Button` primitive for app actions.
 *
 * @example Basic usage
 * ```tsx
 * import { Button } from '@/shared/components'
 *
 * <Button>Continue</Button>
 * <Button variant="primary-dark" size="sm">Save</Button>
 * <Button variant="action" className="w-full">Submit</Button>
 * ```
 *
 * Variants: `primary` (default), `primary-dark`, `action`, `light`, `secondary`, `dark`.
 * Sizes: `default`, `sm`.
 *
 * @example Icons — `icon` prop (default placement: before label)
 * ```tsx
 * import PasskeyIcon from '@/assets/icons/passkey.svg'
 *
 * <Button icon={PasskeyIcon}>Login with SSO</Button>
 * <Button icon={PasskeyIcon} iconPosition="end">Next</Button>
 * ```
 *
 * @example Icons — compound `Button.Icon`
 * ```tsx
 * <Button>
 *   <Button.Icon src={PasskeyIcon} />
 *   Login with SSO
 * </Button>
 *
 * <Button>
 *   Continue
 *   <Button.Icon>{ArrowIcon}</Button.Icon>
 * </Button>
 * ```
 *
 * Icon notes:
 * - Vite SVG imports are URL strings — pass the import directly (`icon={PasskeyIcon}` or
 *   `src={PasskeyIcon}`). Do not render them as components (`<PasskeyIcon />`).
 * - Imported SVGs are tinted via CSS mask so the icon matches the button text colour.
 * - Inline SVG children inherit colour through `currentColor` / `fill-current`.
 *
 * `buttonVariants` (in `button-variants.ts`) is exported for composing custom elements. Use `asChild` to render as a
 * child element (e.g. a router link) without the built-in `icon` prop layout.
 */
import { Slot } from '@radix-ui/react-slot'
import type { VariantProps } from 'class-variance-authority'
import {
  createContext,
  forwardRef,
  isValidElement,
  useContext,
  type ButtonHTMLAttributes,
  type ComponentProps,
  type ReactNode,
} from 'react'

import { cn } from '@/lib/utils'
import { buttonVariants } from '@/shared/components/button-variants'

type ButtonSize = NonNullable<VariantProps<typeof buttonVariants>['size']>

const buttonIconSizeClasses: Record<ButtonSize, string> = {
  default: 'size-5',
  sm: 'size-4',
}

const buttonIconSvgClasses =
  '[&_svg]:size-full [&_svg]:shrink-0 [&_path]:!fill-current [&_circle]:!fill-current [&_rect]:!fill-current'

const ButtonContext = createContext<{ size: ButtonSize }>({ size: 'default' })

const isIconSrc = (value: unknown): value is string => typeof value === 'string'

type ButtonIconProps = Omit<ComponentProps<'span'>, 'children'> & {
  src?: string
  children?: ReactNode
}

const renderButtonIconContent = (
  content: ReactNode,
  size: ButtonSize,
  imageClassName?: string
) => {
  if (isIconSrc(content)) {
    return (
      <span
        className={cn(buttonIconSizeClasses[size], 'bg-current', imageClassName)}
        style={{
          maskImage: `url(${JSON.stringify(content)})`,
          WebkitMaskImage: `url(${JSON.stringify(content)})`,
          maskSize: 'contain',
          WebkitMaskSize: 'contain',
          maskRepeat: 'no-repeat',
          WebkitMaskRepeat: 'no-repeat',
          maskPosition: 'center',
          WebkitMaskPosition: 'center',
        }}
      />
    )
  }

  if (isValidElement(content) && content.type === 'img') {
    return content
  }

  return content
}

const ButtonIcon = forwardRef<HTMLSpanElement, ButtonIconProps>(
  ({ className, src, children, ...props }, ref) => {
    const { size } = useContext(ButtonContext)
    const iconContent = renderButtonIconContent(src ?? children, size)
    const iconSrc = src ?? children

    return (
      <span
        ref={ref}
        aria-hidden
        data-slot="button-icon"
        className={cn(
          'inline-flex shrink-0 items-center justify-center text-inherit',
          !isIconSrc(iconSrc) && buttonIconSizeClasses[size],
          !isIconSrc(iconSrc) && buttonIconSvgClasses,
          className
        )}
        {...props}
      >
        {iconContent}
      </span>
    )
  }
)

ButtonIcon.displayName = 'ButtonIcon'

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
    icon?: ReactNode
    iconPosition?: 'start' | 'end'
  }

const ButtonRoot = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size = 'default',
      asChild = false,
      icon,
      iconPosition = 'start',
      children,
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : 'button'
    const resolvedSize = size ?? 'default'

    const iconElement = icon ? (
      <ButtonIcon src={isIconSrc(icon) ? icon : undefined}>
        {!isIconSrc(icon) ? icon : null}
      </ButtonIcon>
    ) : null

    const content = asChild ? (
      children
    ) : (
      <>
        {iconPosition === 'start' && iconElement}
        {children}
        {iconPosition === 'end' && iconElement}
      </>
    )

    return (
      <ButtonContext.Provider value={{ size: resolvedSize }}>
        <Comp
          ref={ref}
          className={cn(buttonVariants({ variant, size }), className)}
          {...props}
        >
          {content}
        </Comp>
      </ButtonContext.Provider>
    )
  }
)

ButtonRoot.displayName = 'Button'

type ButtonComponent = typeof ButtonRoot & { Icon: typeof ButtonIcon }

const Button = ButtonRoot as ButtonComponent
Button.Icon = ButtonIcon

export { Button, ButtonIcon, type ButtonIconProps, type ButtonProps }
